import { useMemo } from 'react';
import dayjs from 'dayjs';
import { FILTER_VALUES } from '../constants/FilterConstants';

const filterByOption = (data = [], selected = [], accessor) => {
  if (!selected?.length || !accessor) return data;
  if (selected.every((option) => option?.value == null && option?.id == null)) return data;
  if (selected.some((option) => option?.value === '*' || option?.id === '*')) return data;
  const compareVal = (option) => option?.value ?? option?.id;
  return data.filter((item) => selected.some((option) => compareVal(option) === item[accessor]));
};

const filterByStartEndDates = (data = [], startDateVal, endDateVal, startDateAccessor = 'startDate') => {
  const start = startDateVal ? dayjs(startDateVal) : null;
  const end = endDateVal ? dayjs(endDateVal) : null;
  if (!start?.isValid() && !end?.isValid()) return data;
  return data.filter((item) => {
    const rowDate = item[startDateAccessor] ? dayjs(item[startDateAccessor]) : null;
    if (!rowDate || !rowDate.isValid()) return false;
    if (start?.isValid() && rowDate.isBefore(start, 'day')) return false;
    if (end?.isValid() && rowDate.isAfter(end, 'day')) return false;
    return true;
  });
};

const defaultComparator = (a, b) => {
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  return String(a).localeCompare(String(b));
};

const normalizeForCompare = (value) => {
  if (value == null) return value;
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
};

const useMonthlyReconciliation = ({
  data = [],
  searchText = '',
  filterParams = {},
  config,
  sortBy,
  sortComparators = {},
  disabled = false,
}) =>
  useMemo(() => {
    if (disabled || !config) return data;

    const {
      searchFields = [],
      productKey = FILTER_VALUES.PRODUCT_TYPE,
      productAccessor = 'productType',
      calendarKey = FILTER_VALUES.CALENDAR_TYPE,
      calendarAccessor = 'calendarType',
      monthKey = FILTER_VALUES.MONTH,
      monthAccessor = 'month',
      yearKey = FILTER_VALUES.YEAR,
      yearAccessor = 'year',
      approvedByKey = FILTER_VALUES.APPROVED_BY,
      approvedByAccessor = 'approverName',
      createdByKey = FILTER_VALUES.CREATED_BY,
      createdByAccessor = 'approverId',
      startDateKey = FILTER_VALUES.START_DATE,
      endDateKey = FILTER_VALUES.END_DATE,
      startDateAccessor = 'startDate',
    } = config;

    let filtered = data;

    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter((item) =>
        searchFields.some((field) =>
          String(item?.[field] ?? '')
            .toLowerCase()
            .includes(search),
        ),
      );
    }

    filtered = filterByOption(filtered, filterParams[productKey], productAccessor);

    if (calendarKey) {
      filtered = filterByOption(filtered, filterParams[calendarKey], calendarAccessor);
    }

    if (monthKey) {
      filtered = filterByOption(filtered, filterParams[monthKey], monthAccessor);
    }

    if (yearKey) {
      filtered = filterByOption(filtered, filterParams[yearKey], yearAccessor);
    }

    if (approvedByKey) {
      filtered = filterByOption(filtered, filterParams[approvedByKey], approvedByAccessor);
    }

    if (createdByKey) {
      const createdBySelected = Array.isArray(filterParams[createdByKey])
        ? filterParams[createdByKey]
        : filterParams[createdByKey]
        ? [filterParams[createdByKey]]
        : [];
      filtered = filterByOption(filtered, createdBySelected, createdByAccessor);
    }

    if (startDateKey || endDateKey) {
      filtered = filterByStartEndDates(
        filtered,
        filterParams[startDateKey],
        filterParams[endDateKey],
        startDateAccessor,
      );
    }

    if (!sortBy) return filtered;

    const comparator = sortComparators[sortBy.id] || defaultComparator;

    return [...filtered].sort((a, b) => {
      const aValue = normalizeForCompare(a[sortBy.id]);
      const bValue = normalizeForCompare(b[sortBy.id]);

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      const comparison = comparator(aValue, bValue, a, b);
      return sortBy.desc ? -comparison : comparison;
    });
  }, [data, searchText, filterParams, config, sortBy, sortComparators, disabled]);

export default useMonthlyReconciliation;
